import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, Empty } from "@/components/PageHeader";
import { DatePicker } from "@/components/DatePicker";
import { api, eur, num, fdatetime, errMsg } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";

const exportCsv = (rows) => downloadCsv("transazioni", ["data", "cliente", "importo", "punti", "articoli", "negozio", "scontrino"],
  rows.map((t) => [t.created_at, t.customer_name, t.amount, t.points_earned, t.items, t.store, t.receipt]));

export default function Transactions() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [f, setF] = useState({ search: "", store: "all", date_from: "", date_to: "", min_amount: "", max_amount: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { api.get("/stores").then((r) => setStores(r.data)).catch(() => {}); }, []);

  const fetchPage = (page) => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries({ ...f, store: f.store === "all" ? "" : f.store, page }).filter(([, v]) => v !== ""));
    return api.get("/transactions", { params })
      .then((r) => { setError(""); setData((d) => (page === 0 ? r.data : { ...r.data, items: [...d.items, ...r.data.items] })); })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(() => fetchPage(0), 350);
    return () => clearTimeout(t);
  }, [f]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data?.items || [];
  return (
    <div>
      <PageHeader title="Transazioni e acquisti" subtitle={error ? "Dati non disponibili" : data ? `${num(data.total)} scontrini fedeltà · totale ${eur(data.sum)}` : "Caricamento..."}>
        <Button variant="outline" data-testid="export-csv-button" onClick={() => { exportCsv(rows); toast.success(`Esportate ${rows.length} righe caricate`); }}><Download className="mr-2 h-4 w-4" />Esporta CSV</Button>
      </PageHeader>
      <Panel>
        <div className="grid gap-3 border-b border-slate-100 p-4 md:grid-cols-6">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="tx-search-input" className="pl-9" placeholder="Cliente, carta o n. scontrino" value={f.search} onChange={set("search")} />
          </div>
          <Select value={f.store} onValueChange={(v) => setF({ ...f, store: v })}>
            <SelectTrigger data-testid="tx-store-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-80 bg-white">
              <SelectItem value="all">Tutti i punti vendita</SelectItem>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <DatePicker testId="tx-date-from" placeholder="Dal" value={f.date_from} onChange={(v) => setF({ ...f, date_from: v })} />
          <DatePicker testId="tx-date-to" placeholder="Al" value={f.date_to} onChange={(v) => setF({ ...f, date_to: v })} />
          <div className="flex gap-2">
            <Input type="number" placeholder="€ min" data-testid="tx-min-amount" value={f.min_amount} onChange={set("min_amount")} />
            <Input type="number" placeholder="€ max" data-testid="tx-max-amount" value={f.max_amount} onChange={set("max_amount")} />
          </div>
        </div>
        {error ? (
          <div data-testid="tx-error" className="m-4 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">Impossibile caricare le transazioni: {error}</div>
        ) : !data ? <Empty text="Caricamento transazioni..." /> : rows.length === 0 ? <Empty text="Nessuna transazione trovata." /> : (
          <div className="overflow-x-auto">
            <Table data-testid="transactions-table">
              <TableHeader><TableRow>
                <TableHead>Data</TableHead><TableHead>Cliente</TableHead><TableHead>Punto vendita</TableHead>
                <TableHead>Scontrino</TableHead><TableHead className="text-right">Articoli</TableHead><TableHead className="text-right">Punti</TableHead><TableHead className="text-right">Importo</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((t) => (
                  <TableRow key={t.id} data-testid="tx-row">
                    <TableCell className="whitespace-nowrap text-sm text-slate-500">{fdatetime(t.created_at)}</TableCell>
                    <TableCell><Link to={`/clienti/${t.customer_id}`} className="font-medium text-slate-800 hover:text-blue-600">{t.customer_name}</Link></TableCell>
                    <TableCell className="text-sm text-slate-500">{t.store}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{t.receipt}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{t.items}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-blue-600">+{num(t.points_earned)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{eur(t.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {data && !error && (
          <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 text-xs text-slate-500">
            <span data-testid="tx-shown-count">Mostrate {num(rows.length)} di {num(data.total)}</span>
            {rows.length < Math.min(data.total, 10000) && <Button size="sm" variant="outline" data-testid="tx-load-more" disabled={loading} onClick={() => fetchPage(data.page + 1)}>{loading ? "Caricamento..." : "Carica altre"}</Button>}
          </div>
        )}
      </Panel>
    </div>
  );
}
